import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { selectActiveMatterId } from '@/matter/store/selectors'
import { FileUploadState } from './slice'
import { ReductoJobSafeRead, UserFileRead } from '../schemas'
import { UserFileProcessingBlockedReason } from '@/user_files/schemas/processing-status'
import { isTerminalProcessingStatus } from '@/user_files/utils/processing-status'

/**
 * Derive percent from reducto job pages
 */
export function derivePercent(reductoJob: ReductoJobSafeRead | null): number | null {
  if (reductoJob?.num_pages == null || reductoJob?.total_pages == null) return null
  if (reductoJob.total_pages === 0) return null
  return Math.round((reductoJob.num_pages / reductoJob.total_pages) * 100)
}

// ============================================================================
// Selectors
// ============================================================================

/**
 * Select all files from state
 */
export const selectAllFiles = (state: RootState): Record<string, FileUploadState> => state.uppyFileUploadState.files

/**
 * Select all files as an array
 */
export const selectAllFilesArray = createSelector([selectAllFiles], (files): FileUploadState[] => Object.values(files))

/**
 * Files queued for the matter the user is currently in.
 *
 * Uploads outlive a matter switch — the bytes land in the matter the file was queued for — so the
 * slice holds rows for more than one matter at a time. Every user-facing upload surface reads
 * through this so a matter only ever shows its own uploads.
 */
export const selectActiveMatterFiles = createSelector([selectAllFilesArray, selectActiveMatterId], (allFiles, activeMatterId): FileUploadState[] =>
  allFiles.filter((file) => file.client.matterId === activeMatterId)
)

export const selectUploadFilesByServerFileId = createSelector([selectAllFilesArray], (allFiles): Record<string, FileUploadState> => {
  const filesByServerId: Record<string, FileUploadState> = {}

  allFiles.forEach((file) => {
    const serverFileId = file.client.serverFileId
    if (serverFileId) {
      filesByServerId[serverFileId] = file
    }
  })

  return filesByServerId
})

/**
 * Every file waiting to be picked up by the upload driver, across all matters.
 *
 * Deliberately not matter-scoped: the driver in FileUploadProvider works through the queue no
 * matter which matter the user is looking at, so leaving a matter mid-batch doesn't strand the
 * files behind the batch that was running. Use `selectActiveMatterQueuedFiles` for anything the
 * user sees.
 */
export const selectQueuedFiles = createSelector([selectAllFilesArray], (allFiles): FileUploadState[] =>
  allFiles.filter((f) => f.client.uploadStatus === 'queued')
)

/**
 * Select files in 'queued' status for the active matter.
 */
export const selectActiveMatterQueuedFiles = createSelector([selectActiveMatterFiles], (activeMatterFiles): FileUploadState[] =>
  activeMatterFiles.filter((f) => f.client.uploadStatus === 'queued')
)

/**
 * Select files in the active matter that are past the selected/queued state.
 * The dev upload page shows queued files in its selected-files list instead.
 * Sorted by created_at descending (newest first).
 */
export const selectProcessingFiles = createSelector([selectActiveMatterFiles], (activeMatterFiles): FileUploadState[] =>
  activeMatterFiles
    .filter((f) => f.client.uploadStatus !== 'queued')
    .sort((a, b) => {
      const aTime = a.backend?.record.created_at
      const bTime = b.backend?.record.created_at
      // Files without backend data (still uploading) go to top
      if (!aTime && !bTime) return 0
      if (!aTime) return -1
      if (!bTime) return 1
      // Sort by created_at descending (newest first)
      return new Date(bTime).getTime() - new Date(aTime).getTime()
    })
)

/**
 * Select files shown in the Drive upload drawer.
 * The drawer is the user-files upload status surface for the matter the user is in, so uploads
 * from assistant or future feature-owned surfaces remain visible here — but only the ones queued
 * for this matter.
 */
export const selectUploadDrawerFiles = createSelector([selectActiveMatterFiles], (activeMatterFiles): FileUploadState[] =>
  [...activeMatterFiles].sort((a, b) => {
    const aTime = a.backend?.record.created_at
    const bTime = b.backend?.record.created_at
    // Files without backend data (still uploading) go to top
    if (!aTime && !bTime) return 0
    if (!aTime) return -1
    if (!bTime) return 1
    // Sort by created_at descending (newest first)
    return new Date(bTime).getTime() - new Date(aTime).getTime()
  })
)

/**
 * Select files that need status polling
 * - Upload is complete (uploaded to GCS)
 * - Has a serverFileId
 * - Processing status is not terminal (or not yet known)
 *
 * Deliberately not matter-scoped: a file uploaded just before a matter switch keeps its processing
 * status fresh, so its row is accurate when the user comes back to that matter.
 */
export const selectFilesNeedingPolling = createSelector([selectAllFilesArray], (allFiles): FileUploadState[] =>
  allFiles.filter((f) => {
    // Must have completed upload
    if (f.client.uploadStatus !== 'uploaded') return false
    // Must have server file ID
    if (!f.client.serverFileId) return false

    // Quota waiters are durable and recover at the quota-refresh cadence. Keeping them in the
    // rapid processing poller creates repeated requests that cannot advance the file.
    if (f.backend?.record.processing_blocked_reason === UserFileProcessingBlockedReason.OcrQuota) return false

    // Check if in terminal state
    const status = f.backend?.record.processing_status
    if (isTerminalProcessingStatus(status)) {
      return false
    }

    // Not terminal (or status not yet known), needs polling
    return true
  })
)

/**
 * File ids owned by the drawer/Uppy upload state for status polling.
 * This stays source-specific so a central poller can combine it with other
 * polling sources without knowing about drawer internals.
 */
export const selectIncompleteUploadedFileIds = createSelector([selectFilesNeedingPolling], (files): string[] =>
  files.map((file) => file.client.serverFileId).filter((id): id is string => id !== undefined)
)

/**
 * Memoized: live UserFile records for uploaded files, keyed by their server-assigned
 * id (the persisted UserFile UUID). The backend record (incl. processing_status) is
 * kept fresh here by the polling processor, so this is the authoritative status source
 * for files uploaded in the current session — distinct from the folder-derived
 * selectLoadedUserFilesById, which does not receive these upload-poll updates.
 */
export const selectUserFileRecordsByServerId = createSelector([selectAllFilesArray], (allFiles): Record<string, UserFileRead> => {
  const byId: Record<string, UserFileRead> = {}
  for (const f of allFiles) {
    const id = f.client.serverFileId
    if (id && f.backend?.record) byId[id] = f.backend.record
  }
  return byId
})

/**
 * Check if any files are currently uploading, in any matter.
 *
 * Deliberately not matter-scoped: this is the upload driver's "a batch is already running" gate,
 * and a batch keeps running after the user leaves the matter it belongs to. Use
 * `selectIsUploadingInActiveMatter` for anything the user sees.
 */
export const selectIsUploading = (state: RootState): boolean =>
  Object.values(state.uppyFileUploadState.files).some((f) => f.client.uploadStatus === 'initializing' || f.client.uploadStatus === 'uploading')

/**
 * Check if any files are currently uploading for the matter the user is in.
 */
export const selectIsUploadingInActiveMatter = createSelector([selectActiveMatterFiles], (activeMatterFiles): boolean =>
  activeMatterFiles.some((f) => f.client.uploadStatus === 'initializing' || f.client.uploadStatus === 'uploading')
)

/**
 * Get count of queued files
 */
export const selectQueuedCount = (state: RootState): number => selectQueuedFiles(state).length

/**
 * Get count of files that have completed processing (terminal states)
 */
export const selectCompletedCount = (state: RootState): number =>
  Object.values(state.uppyFileUploadState.files).filter((f) => {
    const status = f.backend?.record.processing_status
    return isTerminalProcessingStatus(status)
  }).length

/**
 * Select a single file by clientTmpId
 */
export const selectFileById = (state: RootState, clientTmpId: string): FileUploadState | undefined => state.uppyFileUploadState.files[clientTmpId]

/**
 * Memoized selector for processing file IDs only.
 * Only triggers re-render when the list of IDs changes (items added/removed),
 * not when individual file contents change.
 */
export const selectProcessingFileIds = createSelector([selectProcessingFiles], (files): string[] => files.map((f) => f.client.clientTmpId))

export const selectUploadDrawerFileIds = createSelector([selectUploadDrawerFiles], (files): string[] => files.map((f) => f.client.clientTmpId))

// ============================================================================
// Upload Drawer UI Selectors
// ============================================================================

export const selectIsDrawerDismissed = (state: RootState): boolean => state.uppyFileUploadState.drawer.userDismissed
export const selectIsDrawerMinimized = (state: RootState): boolean => state.uppyFileUploadState.drawer.userMinimized
